/**
 * Per-class engagement for learners: a star-rating + comment (one per student)
 * and a discussion thread visible to everyone in the class.
 */
import { useState } from 'react';
import { PaperPlaneRight, Star, Trash } from '@phosphor-icons/react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { formatRelative } from '../lib/format';
import { Button, Card, Spinner, cx } from './ui';

function StarRow({
  value,
  onPick,
  disabled,
  size = 26,
}: {
  value: number;
  onPick?: (n: number) => void;
  disabled?: boolean;
  size?: number;
}) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="flex items-center gap-1.5" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled || !onPick}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          onMouseEnter={() => onPick && setHover(n)}
          onClick={() => onPick?.(n)}
          className={cx('transition', onPick && !disabled ? 'cursor-pointer hover:scale-110' : 'cursor-default')}
        >
          <Star size={size} weight={n <= shown ? 'fill' : 'regular'} className={n <= shown ? 'text-amber-400' : 'text-slate-300'} />
        </button>
      ))}
    </div>
  );
}

/**
 * Star rating + optional comment; one per student, editable. Rendered inside a
 * Modal (the popup that appears when a class is completed). `onClose` closes it.
 */
export function ClassFeedback({ moduleId, onClose }: { moduleId: string; onClose: () => void }) {
  const s = useAsync(() => api.myFeedback(moduleId), [moduleId]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Seed local state from the loaded feedback exactly once, before the form
  // becomes interactive — otherwise an early star-click would overwrite an
  // existing rating/comment with the un-seeded empty state.
  const [seeded, setSeeded] = useState(false);
  const mine = s.data?.mine ?? null;
  if (!seeded && s.data) {
    if (mine) {
      setRating(mine.rating);
      setComment(mine.comment ?? '');
    }
    setSeeded(true);
  }

  async function submit() {
    if (!(rating >= 1)) return; // a star is required
    setBusy(true);
    setError(null);
    try {
      await api.saveFeedback(moduleId, rating, comment);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save your rating.');
      setBusy(false);
    }
  }

  if (s.loading || !seeded) {
    return (
      <div className="flex items-center gap-3 py-8 text-sm text-slate-400">
        <Spinner size={16} className="text-brand-teal" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">Your rating helps the instructor improve this class — it takes a second.</p>
      <div className="flex flex-col items-center gap-2 py-3">
        <StarRow value={rating} onPick={setRating} disabled={busy} size={36} />
        <span className="text-xs font-medium text-slate-400">{rating ? `${rating} / 5` : 'Tap a star to rate'}</span>
      </div>
      <textarea
        className="input"
        rows={3}
        placeholder="Add a comment for the instructor (optional)…"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      {error && <p className="text-sm font-medium text-rose-600">{error}</p>}
      <div className="flex justify-end gap-3 pt-1">
        <Button variant="secondary" type="button" onClick={onClose} disabled={busy}>
          Maybe later
        </Button>
        <Button onClick={submit} loading={busy} disabled={rating === 0}>
          {mine ? 'Update feedback' : 'Send feedback'}
        </Button>
      </div>
    </div>
  );
}

/** Class discussion thread — everyone in the class sees and can post. */
export function ClassDiscussion({ moduleId }: { moduleId: string }) {
  const { user } = useAuth();
  const s = useAsync(() => api.comments(moduleId), [moduleId]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInstructor = user?.role === 'instructor';

  async function post() {
    if (!draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.postComment(moduleId, draft.trim());
      setDraft('');
      s.reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not post your comment.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.deleteComment(id);
      s.reload();
    } catch {
      /* ignore */
    }
  }

  const comments = s.data ?? [];

  return (
    <Card className="space-y-4 p-6">
      <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">
        Discussion {comments.length > 0 && <span className="text-slate-400">· {comments.length}</span>}
      </h2>

      <div className="flex flex-col gap-2">
        <textarea
          className="input"
          rows={2}
          placeholder="Ask a question or share something with the class…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <Button icon={PaperPlaneRight} onClick={post} loading={busy} disabled={!draft.trim() || busy}>
            Post
          </Button>
          {error && <span className="text-sm font-medium text-rose-600">{error}</span>}
        </div>
      </div>

      {s.loading ? (
        <p className="text-sm text-slate-400">Loading discussion…</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-400">No comments yet — be the first to start the discussion.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} className="rounded-xl bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-brand-navy">{c.userName}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{formatRelative(c.createdAt)}</span>
                  {(isInstructor || c.userId === user?.id) && (
                    <button
                      type="button"
                      aria-label="Delete comment"
                      className="text-slate-300 transition hover:text-rose-600"
                      onClick={() => void remove(c.id)}
                    >
                      <Trash size={15} />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
